import React, { useMemo, useRef, useState } from 'react'

import { Box, Popover, Stack, Typography, useTheme } from '@mui/material'
import { ChevronDownIcon } from 'lucide-react'

import {
  FIELD_RADIUS,
  MENU_PAPER_SX,
  Skeleton,
  fieldAccentColor,
  fieldBorderColor,
  fieldFill,
  fieldFocusRing,
  fieldHoverRing,
  menuPaperChromeSx,
} from '@repo/ui'

import { getPersonLabel } from 'utils'

// Direct file imports, NOT the `components` barrel — this file is part of that barrel,
// so importing back through it is a cycle (the same note `ProductSelectField` and
// `ActionLine` carry, where it threw at runtime).
import { SearchSelect } from '../SearchSelect/SearchSelect'
import { MultiSelectSummary } from '../fields/SelectField'

export interface UserSelectFieldUser {
  id: string
  first_name?: string | null
  last_name?: string | null
  email?: string | null
  [key: string]: unknown
}

interface UserSelectFieldProps {
  /** Everyone selectable. Fetch upstream so the caller owns the scoping. */
  users: UserSelectFieldUser[]
  /** Ids currently picked. The caller owns the selection (same contract as `UserChecklist`). */
  selectedIds: Set<string>
  onChange: (ids: string[]) => void
  isLoading?: boolean
  /** Trigger text when nothing is picked. */
  placeholder?: string
  /** Shown inside the menu when `users` is empty. */
  emptyMessage?: string
  disabled?: boolean
}

/**
 * VRG-528 — a COLLAPSED people picker: a field that opens a menu of searchable,
 * tickable people.
 *
 * ## Why this exists next to `UserChecklist`
 *
 * `UserChecklist` is the right shape when the list IS the surface — a dedicated Assign
 * modal, where the roster is the only thing on screen and permanently expanded is
 * exactly what you want. It is the wrong shape as ONE FIELD inside a longer form: the
 * Address modal already carries type chips, a label, an address, country/state/city/
 * postcode and two checkboxes, and an always-open search box plus five visible rows made
 * a field most brands never touch the tallest thing in the dialog. Measured on
 * `Settings → Address book → Edit` at 1280x800: the form's natural height was 1124px and
 * is now 871px. (The RENDERED dialog is 736px either way — that is `Modal`'s own
 * `maxHeight: calc(100vh - 64px)` clamp, which is exactly why the unclamped content
 * height is the number worth quoting.)
 *
 * So this is the same DATA, collapsed. `UserChecklist` keeps its callers and is not
 * deprecated; the two are one pattern at two altitudes.
 *
 * ## What it reuses, and why nothing here is hand-rolled
 *
 *  - The MENU is the shared `SearchSelect` (§28): it already owns multi-select, the
 *    built-in search shown by default in multi-select mode, `CheckBox` rows, Select-all,
 *    the All/Selected(N) view filter and Clear-all. Its `preview: 'users'` case draws
 *    `UserBlock`, the same row `UserChecklist` and `AssignUsersModal` draw, so collapsing
 *    the field changed no person's presentation.
 *  - `autoApply` — the selection IS the commit. There is no Apply inside the menu because
 *    the FORM's Save is the commit point (§43: a Save button answers "is this input
 *    valid", and a tick has no invalid intermediate state). `SearchSelect` renders a
 *    "Done" dismiss instead, which is what that flag is for.
 *  - The PAPER spreads `MENU_PAPER_SX` + `menuPaperChromeSx` (§69). A `Popover` is not a
 *    `MuiMenu`, so `theme.ts`'s `MuiMenu.paper` override — including the
 *    `backgroundImage: 'none'` that kills MUI's dark-mode elevation overlay — does not
 *    reach it. Style this paper by hand and it renders visibly lighter than every other
 *    dropdown in the app, which is the exact defect §69 was written for.
 *  - The TRIGGER's fill, radius, border and hover/focus rings come from `fieldChrome`
 *    (§31), so it is the same control as the `Input` above it rather than a lookalike.
 *  - The trigger's summary is `MultiSelectSummary`, measured whole-names-then-`+N`.
 *
 * ## The closed state shows the VALUE
 *
 * A collapsed field that hides its own value is worse than the expanded list it replaced
 * — you would have to open it to find out whether anyone is assigned at all. So the
 * trigger renders the picked names (as many as fit, then `+N`), and only falls back to
 * the placeholder when the selection is genuinely empty.
 */
export const UserSelectField = ({
  users,
  selectedIds,
  onChange,
  isLoading = false,
  placeholder = 'Select people',
  emptyMessage = 'No people to show.',
  disabled = false,
}: UserSelectFieldProps) => {
  const theme = useTheme()
  const triggerRef = useRef<HTMLDivElement | null>(null)
  const [open, setOpen] = useState(false)

  /*
    `SearchSelect` reports OPTIONS back, not ids, so it has to be handed option objects.
    Filtering `users` (rather than holding a parallel array of picked people) means a
    selection can never contain someone who is no longer a company member: the source of
    truth is one list, read two ways.
  */
  const selectedUsers = useMemo(
    () => users.filter(user => selectedIds.has(user.id)),
    [users, selectedIds],
  )

  /*
    `getPersonLabel` is the one resolver `UserBlock` / `OwnerPreview` / the owner header
    already use, so the trigger names a person exactly as the row inside the menu does —
    including the case that matters most here, a member invited by email who has not set
    a name yet (the address book's only manager today is one). A local
    `first + ' ' + last` would render that person as an empty string.
  */
  const labels = useMemo(
    () => selectedUsers.map(user => getPersonLabel(user).primary || 'Unnamed'),
    [selectedUsers],
  )

  const handleOpen = () => {
    if (disabled) return
    setOpen(true)
  }

  return (
    <>
      <Stack
        ref={triggerRef}
        direction="row"
        alignItems="center"
        onClick={handleOpen}
        // Reachable and operable from the keyboard: this is a div acting as a control, so
        // the role, the tab stop and the Enter/Space handler all have to be stated. Without
        // them the only way to assign a location manager would be a mouse.
        role="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={event => {
          if (event.key !== 'Enter' && event.key !== ' ') return
          event.preventDefault()
          handleOpen()
        }}
        sx={{
          // 49 — the FIELD band (§29), matching the `Input`s above it in the same form
          // (measured 49px there). Deliberately not `CONTROL_HEIGHT` (40), which is the
          // page-header band; a field's neighbour is another field. There is no shared
          // FIELD_HEIGHT token today — `Input` sets its own — so this is stated, not
          // derived; see the follow-up note in the ticket.
          minHeight: 49,
          px: 4,
          gap: 2,
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          borderRadius: `${FIELD_RADIUS}px`,
          backgroundColor: fieldFill(theme),
          border: `1px solid ${open ? fieldAccentColor(theme) : fieldBorderColor(theme)}`,
          boxShadow: open ? fieldFocusRing(theme) : 'none',
          transition: 'border-color 150ms ease, box-shadow 150ms ease',
          '&:hover': {
            borderColor: disabled ? fieldBorderColor(theme) : fieldAccentColor(theme),
            boxShadow: disabled ? 'none' : open ? fieldFocusRing(theme) : fieldHoverRing(theme),
          },
          // The trigger is a div, so it gets no focus ring for free. Same treatment as
          // focus-within on a real field, per §31.
          '&:focus-visible': {
            outline: 'none',
            borderColor: fieldAccentColor(theme),
            boxShadow: fieldFocusRing(theme),
          },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, fontSize: '16px' }}>
          {isLoading ? (
            // §19 footprint parity: one text-height bar on the trigger's own text line, so
            // the field does not resize when the people arrive.
            <Skeleton height={16} width="55%" />
          ) : labels.length === 0 ? (
            <Typography variant="body2" color="text.secondary" noWrap>
              {placeholder}
            </Typography>
          ) : (
            <MultiSelectSummary labels={labels} />
          )}
        </Box>
        <ChevronDownIcon
          strokeWidth={1}
          size={20}
          style={{
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 150ms ease',
          }}
        />
      </Stack>

      <Popover
        open={open}
        anchorEl={triggerRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        // Above the modal this field lives in, or the list is drawn behind it.
        sx={{ zIndex: theme.zIndex.modal + 1 }}
        slotProps={{
          paper: {
            sx: {
              // ORDER MATTERS: spacing first, chrome second, local overrides last (§69).
              ...MENU_PAPER_SX,
              ...menuPaperChromeSx(theme),
              // Match the trigger, so the menu reads as that field opened rather than as a
              // detached panel. Read at render time from the live element.
              width: triggerRef.current?.clientWidth,
              p: 3,
            },
          },
        }}
      >
        {users.length === 0 && !isLoading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
            <Typography variant="body3" color="text.secondary">
              {emptyMessage}
            </Typography>
          </Stack>
        ) : (
          <SearchSelect
            items={users}
            selected={selectedUsers}
            // `preview: 'users'` draws `UserBlock`; `fields` is what the built-in search
            // matches on, so a member is findable by either name part or by address.
            searchParams={{ fields: ['first_name', 'last_name', 'email'], preview: 'users' }}
            autoApply
            onChange={items => onChange((items ?? []).map((item: any) => item?.id).filter(Boolean))}
            onClose={() => setOpen(false)}
          />
        )}
      </Popover>
    </>
  )
}
